import React, { memo, useState, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import { Button, DataLayer, FormControl } from '@stape-io/promo-shared-content/components';

import { useClientTranslation } from '@/lib/i18n/client';
import { subscribeToNewsletter } from '@/lib/api/SubscribeApi';
import useIsMobile from '@/lib/hooks/useIsMobile';
import { DogHint } from '@/shared/DogHint';
import { SubscribeType } from '@/shared/Subscribe/types';

import styles from './SubscribeForm.module.scss';

interface SubscribeFormProps extends SubscribeType {
  vertical?: boolean;
}

export const SubscribeForm = memo(({ vertical = false, type = 'footer' }: SubscribeFormProps) => {
  const { t } = useClientTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);
  const isMobile = useIsMobile();

  const successHint = useMemo(
    () => (isMobile || vertical ? <span>{t('footer.youHaveveSubscribed')}</span> : <DogHint message={t('footer.youHaveveSubscribed')} />),
    [isMobile, t]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!email) {
        setMessage({ type: 'error', text: t('footer.emailRequired') });
        return;
      }

      setIsSubmitting(true);
      setMessage(null);

      try {
        await subscribeToNewsletter(email, type);
        setMessage({ type: 'success', text: t('footer.youHaveveSubscribed') });
        setEmail('');
        DataLayer.pushSubscribeHelpdeskClickEvent(email, `helpdesk_${type}`);
      } catch {
        setMessage({ type: 'error', text: t('footer.subscribeError') });
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, t]
  );

  return (
    <div className="text">
      <form className={clsx(styles.subscribeForm, 'text-md', { [styles.vertical]: vertical })} onSubmit={handleSubmit} noValidate>
        <FormControl className={clsx(styles.subscribeFormControl, { [styles.error]: message?.type === 'error' })}>
          <input
            id="email"
            type="email"
            placeholder={t('footer.emailPlaceholder')}
            className={clsx(styles.input)}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
          />
        </FormControl>
        <Button size="md" loading={isSubmitting} className={clsx(styles.subscribeButton, 'text-sm text-bold')} disabled={isSubmitting}>
          {t('footer.subscribeButton')}
        </Button>
      </form>
      <div className={styles.message}>
        {message?.type === 'error' && (
          <span
            className={clsx(styles.errorMessage, {
              [styles.visible]: !!message,
            })}
          >
            {message.text}
          </span>
        )}
        {message?.type === 'success' && (
          <span
            className={clsx(styles.successMessage, {
              [styles.visible]: !!message,
            })}
          >
            {successHint}
          </span>
        )}
      </div>
    </div>
  );
});
